/**
 * The language the current page has declared on <html lang>.
 * @file src/web/shared/hooks/useDocumentLanguage.ts
 *
 * ── Why read the DOM instead of passing a prop ────────────────────────────
 * The public layout wraps two unrelated pages — the intake form and the
 * signing page — and each already owns the language decision: one from
 * `?lang=`, the other from the frozen document plus the reader's choice.
 * Both write it to `document.documentElement.lang`, because they must: it is
 * what screen readers, spellcheckers and hyphenation read.
 *
 * So the language is already published, in the one place that is guaranteed
 * to agree with what the client is looking at. Threading a second copy of it
 * through the router would create something that can disagree with the first,
 * and the layout would then be able to render a Spanish footer under an
 * Arabic form. Reading `lang` cannot drift, because it IS the page's answer.
 *
 * ── Why an observer ───────────────────────────────────────────────────────
 * The client can change language mid-form without navigating. The attribute
 * changes; a plain read on first render would not.
 *
 * @llm-rule WHEN: A shared shell needs the language of the page inside it
 * @llm-rule AVOID: Using this inside the page itself — the page HAS the
 *   language and should use its own state
 */

import { useSyncExternalStore } from 'react';
import { BASE_LANGUAGE, isLanguage, type Language } from '@shared/languages';

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributeFilter: ['lang'] });
  return () => observer.disconnect();
}

function snapshot(): string {
  return document.documentElement.lang;
}

/**
 * The page's language, or the base language when it has not declared one we
 * ship — a footer in a language we have no strings for is not an option, and
 * English is the honest fallback rather than a blank.
 */
export function useDocumentLanguage(): Language {
  const lang = useSyncExternalStore(subscribe, snapshot, () => BASE_LANGUAGE);
  return isLanguage(lang) ? lang : BASE_LANGUAGE;
}
